import { useState } from "react";
import { ScreenContainer } from "../../components";
import { Text, ProgressBar } from "react-native-paper";
import { View, Platform } from "react-native";
import { ScrollableContainer, ActionsContainer, Button } from "../../components";
import { colors } from "../../../theme";
import TextInput from "../../components/TextInput";
import TextInputArea from "../../components/TextInputArea";
import { Controller, useForm, FieldErrors } from "react-hook-form";
import { useRef } from "react";
import { FlatList } from "react-native";
import ActivityDatesFields from "./components/ActivityDatesFields";
import PublicationDateField from "./components/PublicationDateField";
import { FormProvider } from "react-hook-form";
import { contentObject } from "./_utils";
import useSnackBar from "../../hooks/useSnackBar";
import { useErrorScroll } from "../../components/ErrorMessage";
import { openBrowser } from "../../screens/utils";
import { ScreenProps } from "./types";
import { ActivityIndicator } from "react-native";
import { Slug } from "./types";
import LocationAutoComplete from "../../components/LocationAutoComplete";
import { Icon as TextInputIcon } from "../../components/TextInput";
import { gql, useMutation } from "@apollo/client";
import KeyboardAvoidingView from "../../components/KeyboardAvoidingView";
import { placeToFormData } from "../../components/AddressAutoComplete/placeToFormData";
import { GooglePlaceData, GooglePlaceDetail } from "react-native-google-places-autocomplete";
import useAmplitude from "../../hooks/useAmplitude";
import { useQuery } from "@apollo/client";
import { GET_PDR_REQUIREMENTS_QUERY } from "../HomeScreen/queries";

export const CREATE_PDR_CREDIT = gql`
  mutation CreatePdrCredit($input: CreatePdrCreditInput!) {
    createPdrCredit(input: $input) {
      message
      success
    }
  }
`;

export type FormData = {
  totalHours: string;
  publicationDate: string;
  startDate: string;
  endDate: string;
  location: string;
  description: string;
};

export const defaultValues: FormData = {
  totalHours: "",
  publicationDate: "",
  startDate: "",
  endDate: "",
  location: "",
  description: "",
};

function ActivityFormScreen({ navigation, route }: ScreenProps<"ActivityFormScreen">) {
  const { slug } = route.params;
  const flatListRef = useRef<FlatList>(null);
  const [apiError, setApiError] = useState("");
  const [locationSelected, setLocationSelected] = useState(false);
  const { data: pdrRequirements } = useQuery(GET_PDR_REQUIREMENTS_QUERY, {
    fetchPolicy: "cache-first",
  });

  const form = useForm<FormData>({ defaultValues: { ...defaultValues }, shouldFocusError: false });
  const {
    control,
    handleSubmit,
    setValue,
    setFocus,
    getValues,
    formState: { errors, isSubmitting },
  } = form;
  const { Divider, scrollToField, onSubmitError } = useErrorScroll({ defaultValues, flatListRef });
  const [createPdrCredit, { loading }] = useMutation(CREATE_PDR_CREDIT);

  const content = contentObject[`${slug}` as Slug];
  const showSnackBar = useSnackBar();
  const { trackEvent } = useAmplitude();

  const handleOnSubmitError = (errors: FieldErrors<FormData>) => {
    onSubmitError(errors);
    Object.values(errors).map((error) => {
      if (error?.message) {
        trackEvent("progress-report-pdr-error", {
          pdr_activity_category_name: slug,
          error_text: error?.message,
        });
      }
    });
  };

  const onSubmit = async ({ publicationDate, description, totalHours, location, startDate, endDate }: FormData) => {
    trackEvent("click-report-pdr-submit", {
      pdr_activity_category_name: slug,
    });
    try {
      const {
        data: {
          createPdrCredit: { success, message },
        },
      } = await createPdrCredit({
        variables: {
          input: {
            author: Platform.OS === "web" ? "MYPGA" : "MEMAPP",
            ...(slug === "service-to-pga-industry" && { hoursServed: Number(totalHours) }),
            ...(slug === "media-related-articles"
              ? { publishedOn: publicationDate }
              : {
                  startedOn: startDate,
                  endedOn: endDate,
                  location: location,
                }),
            description,
            reportType: slug,
          },
        },
      });
      if (success) {
        setTimeout(() => {
          showSnackBar({ message: "PDR Activity Submitted", icon: "check-circle", duration: 3000 });
          trackEvent("progress-report-pdr-complete", {
            pdr_activity_category_name: slug,
          });
        }, 1000);
        navigation.navigate("PdrHistory", { slug: "pdr-history" });
      } else {
        trackEvent("progress-report-pdr-error", {
          pdr_activity_category_name: slug,
          error_text: message,
        });
        throw new Error(message);
      }
    } catch (e: any) {
      setApiError((e as Error).message || "Something went wrong");
    }
  };

  if (loading) {
    return (
      <ScreenContainer navigationTitle={"Report PDRs"} noBackButton={false} testID="activity-form-screen">
        <ActivityIndicator size="large" className="flex-1 items-center justify-center bg-white mt-10" />
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer navigationTitle={"Report PDRs"}>
      {Platform.OS !== "web" && <ProgressBar progress={0.85} color={colors.primary.light} />}
      <KeyboardAvoidingView>
        <ScrollableContainer ref={flatListRef} testID="activity-form-screen" keyboardShouldPersistTaps="handled">
          <View className="mx-6 mt-6 mb-8">
            <Text className="text-large-tight font-700 text-ink-darkest">Report {content.title} PDR Credits</Text>
            <Text className="text-small-normal font-400 text-ink mt-2">{content.description2}</Text>
          </View>
          <FormProvider {...form}>
            {slug === "service-to-pga-industry" && (
              <>
                <Divider field="totalHours" />
                <View className="mx-6 mb-8">
                  <Text className="text-large-tight font-700 text-ink-darkest mt-6">Total Hours</Text>
                  <Text className="text-small-normal font-400 text-ink mt-2 mb-4">
                    The total number of hours served. Input the total hours across all days.
                  </Text>
                  <View className="w-1/2">
                    <Controller
                      name="totalHours"
                      control={control}
                      rules={{
                        required: "Total hours is required",
                        pattern: {
                          value: /^[0-9]+$/,
                          message: "Must be a number",
                        },
                        max: {
                          value: 99,
                          message: "Must be less than 100 hours",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <TextInput
                          keyboardType="numeric"
                          onChangeText={(text) => onChange(text)}
                          value={value.replace(/\D/g, "")}
                          error={errors["totalHours"]}
                          testID="total-hours"
                          placeholder="Hours"
                          onFocus={() => scrollToField("totalHours")}
                        />
                      )}
                    />
                  </View>
                </View>
              </>
            )}

            {slug === "media-related-articles" ? (
              <>
                <Divider field="publicationDate" />
                <PublicationDateField minDate={pdrRequirements?.currentPerson?.pdrRequirements?.cycleStartsOn} />
              </>
            ) : (
              <>
                <Divider fields={["startDate", "endDate"]} />
                <ActivityDatesFields slug={slug} minDate={pdrRequirements?.currentPerson?.pdrRequirements?.cycleStartsOn} />
                <Divider field="location" />
                <View className="mx-6 mt-6 mb-8">
                  <Text className="text-large-tight font-700 text-ink-darkest mb-2">Location</Text>
                  <Text className="text-small-normal font-400 text-ink  mb-4">
                    Enter the city and state or zip code where the activity occurred.
                  </Text>
                  <View className="md:w-1/2">
                    <Controller
                      name="location"
                      control={control}
                      rules={{
                        required: "Location is required",
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <LocationAutoComplete
                          name="location"
                          label="Location (City or Zip)"
                          type="(regions)"
                          nearbyPlacesAPI="GoogleReverseGeocoding"
                          components="country:us"
                          field={field}
                          onChangeText={(e) => {
                            field.onChange(e);
                            setLocationSelected(false);
                          }}
                          onPress={(_data: GooglePlaceData, details: GooglePlaceDetail) => {
                            const { city, state } = placeToFormData(details);
                            setValue("location", `${city ? `${city},` : ""} ${state}` || "");
                            setLocationSelected(true);
                          }}
                          filterReverseGeocodingByTypes={["locality", "postal_code"]}
                          inputProps={{
                            right: <TextInputIcon icon="map-pin" />,
                          }}
                          onBlur={() => {
                            if (!locationSelected && getValues("location") !== "") {
                              setFocus("location");
                            }
                          }}
                          error={error}
                          testID="location"
                          onFocus={() => scrollToField("location")}
                          setLocationSelected={setLocationSelected}
                        />
                      )}
                    />
                  </View>
                </View>
              </>
            )}
            <Divider />
            <View className="mx-6 mt-6 mb-8">
              <Text className="text-large-tight font-700 text-ink-darkest">Description</Text>
              <Text className="text-small-normal font-400 text-ink mt-2 mb-4">{content.descriptionText}</Text>

              <Controller
                name="description"
                control={control}
                rules={{
                  required: "Description is required",
                }}
                render={({ field: { onChange, value } }) => (
                  <TextInputArea
                    onChangeText={(text) => onChange(text)}
                    value={value}
                    textLimit={255}
                    error={errors["description"]}
                    testID="description"
                    placeholder={content.descriptionPlaceholder}
                    onFocus={() => scrollToField("description")}
                  />
                )}
              />
            </View>
          </FormProvider>
          <Divider />
          <View className="mx-6 pt-6 pb-8 md:mb-3">
            <Text className="text-small-normal font-400 text-ink">
              Per the{" "}
              <Text
                className="underline text-primary"
                onPress={openBrowser("https://resources.pga.org/uploads/code-of-ethics-2024.pdf")}
              >
                Code of Ethics,
              </Text>{" "}
              members are trusted to be factual in submission of information.
            </Text>
          </View>
          <ActionsContainer errorAlert errorMessage={apiError} onCloseErrorMessage={setApiError}>
            <Button
              testID="submit-button"
              loading={loading}
              disabled={isSubmitting}
              onPress={handleSubmit(onSubmit, handleOnSubmitError)}
              mode="contained"
            >
              Submit
            </Button>
          </ActionsContainer>
        </ScrollableContainer>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}

export default ActivityFormScreen;
